import type { FC, ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'

import { cn } from '@/lib/utils'

export type SellerStat = {
    label: string
    value: string
    icon: LucideIcon
}

interface SellerStatsProps {
    stats: Array<SellerStat>
    className?: string
}

/**
 * The stat strip under the verification row. Every entry is built on the
 * server from real data, and a stat with nothing behind it is never passed in,
 * so this component has no placeholder or zero-state of its own.
 */
export const SellerStats: FC<SellerStatsProps> = ({ stats, className }): ReactNode => {
    return (
        <dl
            className={cn(
                'grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:items-start sm:gap-x-0 sm:gap-y-5',
                className
            )}
        >
            {stats.map((stat, index) => {
                const Icon = stat.icon

                return (
                    <div
                        key={stat.label}
                        className={cn(
                            'min-w-0 rounded-mta-xs bg-surface-muted/60 p-3 sm:rounded-none sm:bg-transparent sm:p-0 sm:pr-6 lg:pr-8',
                            index > 0 && 'sm:border-l sm:border-ink-900/15 sm:pl-6 lg:pl-8'
                        )}
                    >
                        <dt className='type-eyebrow text-text-muted'>
                            {stat.label}
                        </dt>

                        <dd className='mt-2.5 flex items-center gap-2 text-[0.9375rem] font-semibold leading-none text-ink-900'>
                            <Icon className='size-[1.15rem] shrink-0 text-ink-900' aria-hidden='true' strokeWidth={1.75} />
                            <span className='truncate'>{stat.value}</span>
                        </dd>
                    </div>
                )
            })}
        </dl>
    )
}
